/**
 * FeedbackForm — 불편/오류/건의사항 신고 단일 단계 폼
 *
 * 로그인 사용자 전용. 회사·이름·이메일·연락처는 백엔드 세션에서 자동 추출하므로
 * 클라이언트 폼은 textarea 한 개와 등록·취소 버튼만 노출한다.
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Field, PrimaryButton, SecondaryButton, inputClass } from './QuoteForm';
import {
  MAX_LENGTHS,
  checkFieldSafety,
  describeIssue,
  normalizeInput,
} from '@/lib/utils/sanitize';
import type { FeedbackFormData } from '@/types/qa';

interface FeedbackFormProps {
  formData: Partial<FeedbackFormData>;
  onSubmit: (data: FeedbackFormData) => void;
  onCancel: () => void;
}

const MIN_LENGTH = 10;

export function FeedbackForm({ formData, onSubmit, onCancel }: FeedbackFormProps) {
  const [message, setMessage] = useState<string>(formData.message ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [safetyError, setSafetyError] = useState<string | null>(null);

  const trimmedLength = message.trim().length;
  const canSubmit = trimmedLength >= MIN_LENGTH && !submitting;

  function handleChange(value: string) {
    setMessage(normalizeInput(value));
    if (safetyError) setSafetyError(null);
  }

  function handleSubmit() {
    if (!canSubmit) return;
    const { issue } = checkFieldSafety(message, 'message', MAX_LENGTHS.message);
    if (issue) {
      setSafetyError(describeIssue(issue));
      return;
    }
    setSubmitting(true);
    onSubmit({ message: message.trim() });
  }

  return (
    <div className="ml-flex ml-flex-col ml-gap-2">
      <div className="ml-flex ml-items-center ml-gap-2 ml-mb-1">
        <span
          className={cn(
            'ml-inline-flex ml-items-center ml-justify-center',
            'ml-w-5 ml-h-5 ml-rounded-full',
            'ml-bg-brand-600 ml-text-white ml-text-cb-xs ml-font-semibold'
          )}
          aria-hidden="true"
        >
          !
        </span>
        <span className="ml-text-cb-base ml-font-semibold ml-text-ink">
          불편/오류/건의사항을 알려주세요
        </span>
      </div>

      <div className="ml-text-cb-sm ml-text-mute ml-mb-1 ml-leading-relaxed">
        회원 정보는 자동으로 함께 전달되니 신고 내용만 작성해 주세요.
      </div>

      <Field label="신고 내용" required>
        <textarea
          value={message}
          onChange={(e) => handleChange(e.target.value)}
          rows={5}
          placeholder={`최소 ${MIN_LENGTH}자 이상 입력해 주세요. 발생한 화면, 시간, 재현 방법 등을 함께 적어 주시면 빠른 처리에 도움이 됩니다.`}
          maxLength={MAX_LENGTHS.message}
          className={cn(inputClass, 'ml-resize-none')}
        />
      </Field>

      <div className="ml-flex ml-justify-end ml-text-cb-xs ml-text-mute">
        {message.length} / {MAX_LENGTHS.message}
      </div>

      {safetyError && (
        <div
          role="alert"
          className={cn(
            'ml-mt-1 ml-p-2.5 ml-rounded-cb-sm',
            'ml-bg-surface ml-border ml-border-danger',
            'ml-text-cb-sm ml-text-danger ml-whitespace-pre-line'
          )}
        >
          {safetyError}
        </div>
      )}

      <div className="ml-flex ml-gap-2 ml-mt-2">
        <SecondaryButton onClick={onCancel}>취소</SecondaryButton>
        <PrimaryButton disabled={!canSubmit} onClick={handleSubmit}>
          {submitting ? '제출 중...' : '등록'}
        </PrimaryButton>
      </div>
    </div>
  );
}
